import { Injectable, Inject } from '@angular/core';
import { Observable } from 'rxjs';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { HttpParams } from '@angular/common/http';
import { Betriebskontakt, BetriebSuchResult } from './betriebskontakt';

@Injectable({ providedIn: 'root' })
export class BetriebskontakteService {
    private readonly baseUrl: string;

    constructor(
        private api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {
        this.baseUrl = `${this.apiBaseUrl}benutzer_betriebskontakte/`;
    }

    /**
     * Lädt alle Betriebskontakte des aktuellen Benutzers
     */
    getBetriebskontakte(): Observable<{ records: Betriebskontakt[]; count: number }> {
        return this.api.getAll(`${this.baseUrl}read.php`);
    }

    /**
     * Admin-Methode: Lädt alle Kontakte eines Betriebs von allen Benutzern
     * @param betriebId Die Betriebs-ID
     */
    getAlleKontakteFuerBetrieb(betriebId: number): Observable<{ records: Betriebskontakt[]; count: number }> {
        const params = new HttpParams().set('betrieb_id', betriebId.toString());
        return this.api.getAll(`${this.baseUrl}read_by_betrieb_id.php`, params);
    }

    /**
     * Lädt einen spezifischen Betriebskontakt
     */
    getBetriebskontakt(kontaktId: number): Observable<Betriebskontakt> {
        const params = new HttpParams().set('id', kontaktId.toString());
        return this.api.get(`${this.baseUrl}read_one.php`, params);
    }

    /**
     * Lädt einen spezifischen Betriebskontakt und gibt direkt das Objekt zurück
     */
    getBetriebskontaktById(kundeId: number): Observable<Betriebskontakt> {
        const params = new HttpParams().set('kunde_id', kundeId.toString());
        return this.api.get(`${this.baseUrl}read_by_kunde_id.php`, params);
    }

    /**
     * Sucht nach Betrieben basierend auf VVVO oder Bezeichnung
     * Verwendet eine spezielle API für alle Betriebe
     */
    sucheBetriebe(suchbegriff: string): Observable<{ records: BetriebSuchResult[]; count: number }> {
        const params = new HttpParams().set('search', suchbegriff);
        return this.api.getAll(`${this.baseUrl}search_betriebe.php`, params);
    }

    /**
     * Erstellt einen neuen Betriebskontakt
     * Kann sowohl bestehende Kunden (kunde_id) als auch neue Betriebe (vvvo + bezeichnung) handhaben
     */
    erstelleBetriebskontakt(
        kontakt:
            | Partial<Betriebskontakt>
            | {
                  vvvo: string;
                  bezeichnung: string;
                  zusatz?: string;
                  strasse?: string;
                  plz?: string;
                  ort?: string;
                  ortsteil?: string;
                  email?: string;
                  telefon_1?: string;
                  telefon_2?: string;
                  mobil_1?: string;
                  mobil_2?: string;
                  favorit?: boolean;
              },
    ): Observable<any> {
        return this.api.post(`${this.baseUrl}create.php`, kontakt);
    }

    /**
     * Aktualisiert einen bestehenden Betriebskontakt
     */
    aktualisiereBetriebskontakt(kontakt: Betriebskontakt): Observable<any> {
        return this.api.post(`${this.baseUrl}update.php`, kontakt);
    }

    /**
     * Löscht einen Betriebskontakt
     */
    loescheBetriebskontakt(kontaktId: number): Observable<any> {
        return this.api.deleteEntity(`${this.baseUrl}delete.php`, { kontakt_id: kontaktId });
    }

    /**
     * Fügt einen bestehenden Betrieb als Kontakt hinzu
     */
    fuegeKontaktHinzu(kundeId: number, favorit: boolean = false): Observable<any> {
        const kontakt: Partial<Betriebskontakt> = {
            kunde_id: kundeId,
            favorit: favorit ? 1 : 0,
            ist_kunde: 0, // Standard: kein Kunde im System
        };
        return this.erstelleBetriebskontakt(kontakt);
    }

    /**
     * Erstellt einen neuen Betrieb und Kontakt
     */
    erstelleNeuenBetriebUndKontakt(betriebsdaten: {
        vvvo: string;
        bezeichnung: string;
        zusatz?: string;
        strasse?: string;
        plz?: string;
        ort?: string;
        ortsteil?: string;
        email?: string;
        telefon_1?: string;
        telefon_2?: string;
        mobil_1?: string;
        mobil_2?: string;
        favorit?: boolean;
    }): Observable<any> {
        return this.erstelleBetriebskontakt(betriebsdaten);
    }

    /**
     * Togglet den Favoriten-Status eines Kontakts
     */
    toggleFavorit(kontakt: Betriebskontakt): Observable<any> {
        const updatedKontakt = { ...kontakt };
        updatedKontakt.favorit = kontakt.favorit === 1 ? 0 : 1;
        return this.aktualisiereBetriebskontakt(updatedKontakt);
    }
}
